import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '@app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { IDialogResult } from '@app/components/shared/confirmation-dialog/model/dialog-models';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { PasswordService } from '@app/services/password/password.service';
import { defined } from '@app/fremework/functions';
import { UserToken } from '@app/services/password/model/user-token';
import { SelectedRaceService } from '@app/services/selected-race.service';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private readonly loginDialogId = 'login-dialog';

  constructor(
    private confirmationDialog: MatDialog,
    private passwordService: PasswordService,
    private selectedRaceService: SelectedRaceService) {
  }

  hasToken(raceGuid: string): boolean {
    return defined(this.passwordService.getToken(raceGuid));
  }

  checkLogin(raceGuid: string): Observable<UserToken> {

    const userToken = this.passwordService.getUserToken(raceGuid);
    if (userToken) {
      this.selectedRaceService.setSelectedRaceGuid(raceGuid, true);
      return of(userToken);
    }

    return this.openLogin()
      .afterClosed()
      .pipe(
        take(1),
        switchMap(dialogResult => {
            if (!dialogResult?.inputMap) {
              return of(undefined);
            }
            return this.passwordService
              .requestToken(
                dialogResult.inputMap.get('user')?.toLowerCase().trim(),
                dialogResult.inputMap.get('pwd')?.toLowerCase().trim(),
                dialogResult.inputMap.get('sign')?.trim(),
                raceGuid)
              .pipe(take(1), map(userToken => {
                this.passwordService.setToken(userToken);
                this.selectedRaceService.setSelectedRaceGuid(raceGuid, true);
                return defined(userToken?.token) ? userToken : undefined;
              }));
          }
        ));
  }

  openLogin = (): MatDialogRef<ConfirmationDialogComponent<IDialogResult>, IDialogResult> => {
    const existingDialog = this.confirmationDialog.getDialogById(this.loginDialogId);
    if (existingDialog) {
      return existingDialog as MatDialogRef<
        ConfirmationDialogComponent<IDialogResult>,
        IDialogResult
      >;
    }

    return this.confirmationDialog.open(ConfirmationDialogComponent, {
      id: this.loginDialogId,
      data: {
        title: 'Ange tävlingslösenord',
        message: undefined,
        bulletList: undefined,
        primaryButton: 'OK',
        secondaryButton: 'Avbryt',
        correlationId: 'login',
        inputs: [
          { label: 'Användarnamn', hint: 'användarnamn', keyValuepair: { key: 'user', value: '' }, autocomplete: 'username', name: 'username', inputType: 'text' },
          { label: 'Lösenord', hint: 'lösenord', keyValuepair: { key: 'pwd', value: '' }, autocomplete: 'current-password', name: 'password', inputType: 'password' },
          { label: 'Signatur namn', hint: 'signatur', keyValuepair: { key: 'sign', value: '' }, autocomplete: 'name', name: 'name', inputType: 'text' }]
      }
    });
  };

}
